/** Display-name step for welcome onboarding ("Hoe mag ik je noemen?"). */
import { useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useT } from '@/i18n/useT';
import { ONBOARDING_WELCOME_STEP_TEST_IDS } from '@/features/onboarding/constants/welcomeStepTestIds';
import {
  ONBOARDING_DISPLAY_NAME_INPUT_BUFFER,
  ONBOARDING_DISPLAY_NAME_MAX_LENGTH,
  validateOnboardingDisplayName,
} from '@/features/onboarding/utils/onboardingDisplayNameUtils';
import { ONBOARDING_MARKETING_OPT_IN_TEST_ID } from '@/features/onboarding/constants/onboardingMarketingOptIn';

/**
 * @param {Object} props
 * @param {string} props.initialValue
 * @param {boolean} props.saving
 * @param {string | null} props.errorKey
 * @param {(payload: { displayName: string; marketingOptIn: boolean }) => void | Promise<void>} props.onSubmit
 */
const OnboardingWelcomeDisplayNameStep = ({
  initialValue,
  saving,
  errorKey,
  onSubmit,
}) => {
  const t = useT();
  const inputRef = useRef(null);
  const [value, setValue] = useState(initialValue);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [localErrorKey, setLocalErrorKey] = useState(null);

  useLayoutEffect(() => {
    const frameId = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frameId);
  }, []);

  const trimmed = value.trim();
  const canSubmit = trimmed.length > 0 && !saving;
  const alertKey = errorKey ?? localErrorKey;

  const handleSubmit = () => {
    if (saving) {
      return;
    }
    const validation = validateOnboardingDisplayName(value);
    if (!validation.ok) {
      setLocalErrorKey(
        validation.reason === 'tooLong'
          ? 'onboarding.displayNameStep.errorTooLong'
          : 'onboarding.displayNameStep.errorEmpty',
      );
      return;
    }
    setLocalErrorKey(null);
    void onSubmit({
      displayName: validation.value,
      marketingOptIn,
    });
  };

  return (
    <Box data-testid={ONBOARDING_WELCOME_STEP_TEST_IDS.displayName}>
      <Typography variant="h5" component="h1" gutterBottom>
        {t('onboarding.displayNameStep.title', 'Hoe mag ik je noemen?')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          'onboarding.displayNameStep.body',
          'Een voornaam is genoeg — zo spreek ik je persoonlijk aan.',
        )}
      </Typography>
      <TextField
        fullWidth
        size="small"
        inputRef={inputRef}
        label={t('onboarding.displayNameStep.label', 'Je naam')}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          if (localErrorKey) {
            setLocalErrorKey(null);
          }
        }}
        inputProps={{
          maxLength:
            ONBOARDING_DISPLAY_NAME_MAX_LENGTH +
            ONBOARDING_DISPLAY_NAME_INPUT_BUFFER,
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (canSubmit) {
              handleSubmit();
            }
          }
        }}
        error={Boolean(alertKey)}
        helperText={
          alertKey
            ? t(
                alertKey,
                alertKey === 'onboarding.displayNameStep.errorTooLong'
                  ? 'Gebruik maximaal 50 tekens.'
                  : alertKey === 'onboarding.displayNameStep.errorSave'
                    ? 'Opslaan is mislukt. Probeer het opnieuw.'
                    : 'Vul je naam in om verder te gaan.',
              )
            : undefined
        }
        FormHelperTextProps={
          alertKey ? { role: 'alert', 'aria-live': 'polite' } : undefined
        }
      />
      <FormControlLabel
        sx={{ mt: 1.5, alignItems: 'flex-start' }}
        control={
          <Checkbox
            checked={marketingOptIn}
            onChange={(e) => setMarketingOptIn(e.target.checked)}
            inputProps={{
              'data-testid': ONBOARDING_MARKETING_OPT_IN_TEST_ID,
            }}
          />
        }
        label={
          <Typography variant="body2" color="text.secondary">
            {t(
              'onboarding.displayNameStep.marketingOptIn',
              'Houd me op de hoogte van nieuws en updates over Riposo.',
            )}
          </Typography>
        }
      />
      <Button
        variant="contained"
        sx={{ mt: 2 }}
        disabled={!canSubmit}
        onClick={handleSubmit}
      >
        {t('onboarding.displayNameStep.submit', 'Verder')}
      </Button>
    </Box>
  );
};

export default OnboardingWelcomeDisplayNameStep;
